import { define } from "@chatbotgang/etude/util/define";
import { z } from "zod";

namespace safeSchema {
  export type Output<TOutput = any, TPreOutput = unknown> =
    | {
        isSuccess: false;
        isError: true;
        raw: TPreOutput;
      }
    | {
        isSuccess: true;
        isError: false;
        data: TOutput;
      };
}

/**
 * Transforms a Zod schema to always return an object containing a result,
 * ensuring the original schema's output is preserved while preventing any
 * parsing errors.
 *
 * Ported from Zeffiroso (`packages/utils/zod/safeSchema.ts`); the only change
 * is importing `zod` rather than `zod/v3`, so this package uses the same zod
 * instance as the rest of the repo.
 */
function safeSchema<
  TSchema extends z.ZodType,
  TPreSchema extends z.ZodType = z.ZodType<unknown>,
>(
  schema: TSchema,
  /**
   * If provided, the preSchema will be used to parse the input before applying
   * the main schema.
   *
   * This can be useful for narrowing the type of the raw input.
   */
  preSchema?: TPreSchema,
) {
  const newSchema = schema
    .transform((data) =>
      define<safeSchema.Output<z.output<TSchema>, z.output<TPreSchema>>>({
        isSuccess: true,
        isError: false,
        data,
      }),
    )
    .catch((ctx) => {
      return {
        isSuccess: false,
        isError: true,
        raw: ctx.input as unknown as z.output<TPreSchema>,
      };
    });
  if (preSchema) {
    return z.pipeline(preSchema, newSchema);
  }
  return newSchema;
}

/**
 * Extracts the raw value from a safeSchema output.
 * Returns `data` if successful, otherwise returns `raw`.
 * Returns `null` for null input, `undefined` for undefined input.
 */
function unwrapSafeSchema<TOutput, TPreOutput>(
  value: safeSchema.Output<TOutput, TPreOutput> | null | undefined,
): TOutput | TPreOutput | null | undefined {
  if (value === null) return null;
  if (value === undefined) return undefined;
  return value.isSuccess ? value.data : value.raw;
}

export { safeSchema, unwrapSafeSchema };
